import clsx from "clsx";
import { Tooltip } from "react-tooltip";
import {
  ICPSettingsOverride,
  IToggleButton,
  IToggleButtonToReturnBoolean,
} from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";
import { ITradeSettingsStore } from "@/store/tradeSettingsStore";
import {
  setCPSettingToggleOverride,
  setActiveToggle,
} from "@/store/tradeSettingsStoreACTIONS";
import { resolveOverrideBorder } from "./CPSettingsGridHelper";
import { TRADE_SETTINGS_CONTROL_HEIGHT } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";

// Exported so the blotter's Trade-Wide Settings popover
// (TradeWideSettingsCellRenderer) can reuse the exact same trade-level toggle
// control + setActiveToggle wiring. When no cpCode is passed it mutates the
// trade-level setting (correct for cpOverridable:false settings like Auto
// Affirm / Cover Spread).
export function SettingDropdown({
  settingArray,
  esid,
  settingField,
  cpCode,
  fontSizeClass,
  disabled,
  isOverridden,
  isPending,
  isFollowingPending,
  tooltipId,
  tooltipMessage,
  isOptionDisabled,
  onSelect,
}: {
  settingArray: IToggleButton[] | IToggleButtonToReturnBoolean[];
  esid: string;
  settingField: keyof ICPSettingsOverride & keyof ITradeSettingsStore;
  cpCode?: string;
  fontSizeClass: string;
  disabled?: boolean;
  isOverridden?: boolean;
  isPending?: boolean;
  isFollowingPending?: boolean;
  // Per-option gating (e.g. flag-gated expiry types render disabled, LQC-2170).
  isOptionDisabled?: (optionName: string) => boolean;
  // Take the selection instead of writing to tradeSettingsStore. For callers
  // holding their own draft state (LQC-2172's edit-expiry modal), where a
  // trade-level write would leak into the New/Edit panel for the same esid.
  onSelect?: (selected: IToggleButton | IToggleButtonToReturnBoolean) => void;
  // Optional hover tooltip (e.g. the single-CP Cover lock message). Mirrors
  // SettingNumericInput: the id sits on a wrapper div so it still fires when the
  // select itself is disabled.
  tooltipId?: string;
  tooltipMessage?: string;
}) {
  const activeValue = settingArray.find((t) => t.active)?.name || "";
  const borderColor = resolveOverrideBorder(
    isOverridden,
    isPending,
    isFollowingPending,
  );

  const selectEl = (
    <select
      disabled={disabled}
      className={clsx(
        TRADE_SETTINGS_CONTROL_HEIGHT,
        "w-full rounded-md border bg-transparent px-1 dark:bg-gray-800 dark:text-gray-200",
        !borderColor && "border-gray-300 dark:border-gray-600",
        fontSizeClass,
        disabled && "cursor-not-allowed opacity-50",
      )}
      style={borderColor ? { borderColor } : undefined}
      value={activeValue}
      onChange={(e) => {
        const selected = settingArray.find((t) => t.name === e.target.value);
        if (!selected) return;
        if (onSelect) {
          onSelect(selected);
        } else if (cpCode) {
          setCPSettingToggleOverride({
            esid,
            cpCode,
            settingField,
            selectedElement: selected,
            stateVariable: settingArray,
          });
        } else {
          setActiveToggle({
            esid,
            selectedElement: selected,
            stateVariable: settingArray,
            settingField,
          });
        }
        e.target.blur();
      }}
    >
      {settingArray.map((type) => {
        // Label says why rather than a tooltip: an open native select popup is
        // OS-drawn, so its options can't host one.
        const optionDisabled = isOptionDisabled?.(type.name);
        return (
          <option key={type.name} value={type.name} disabled={optionDisabled}>
            {optionDisabled ? `${type.name} (unavailable)` : type.name}
          </option>
        );
      })}
    </select>
  );

  const showTooltip = !!tooltipId && !!tooltipMessage;
  if (!showTooltip) return selectEl;

  return (
    <>
      <div className="w-full" data-tooltip-id={tooltipId}>
        {selectEl}
      </div>
      <Tooltip
        id={tooltipId}
        place="top"
        delayShow={100}
        className="z-50 !text-xs"
      >
        {tooltipMessage}
      </Tooltip>
    </>
  );
}

export default SettingDropdown;
